import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChefHat, Download, ExternalLink, Mail, Star, Newspaper, Loader2 } from "lucide-react";
import SocialShare from "@/components/SocialShare";
import { Button } from "@/components/ui/button";
import { chefPortraitFor } from "@/lib/chefAvatar";

const SCREENSHOTS = [
  { label: "Dashboard: Recipe P&L", src: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1600&q=80" },
  { label: "Recipe detail: cost breakdown", src: "https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1600&q=80" },
  { label: "Roster: weekly labour cost", src: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=80" },
];

const FACTS = [
  { label: "Ingredient units supported", value: "14+" },
  { label: "Cost math accuracy", value: "±0.01" },
  { label: "Locales auto-detected", value: "40" },
  { label: "Shipped", value: "2026" },
];

const BOILERPLATE = "CulinaireSpecialist is a menu-costing platform for restaurants. It reconciles weight, volume, count, yield and labour into per-plate economics a chef can price against. Built by working chefs, for working chefs.";

function resolvePhoto(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch { /* toast handled inline */ }
    finally { setSending(false); }
  };
  if (sent) return (
    <div className="glass-card rounded-2xl p-6 text-center" data-testid="contact-form-sent">
      <div className="label-eyebrow gold mb-2">Message received</div>
      <div className="font-display text-2xl">Thank you.</div>
      <p className="text-sm text-muted-foreground mt-2">We reply inside one business day.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="glass-card rounded-2xl p-5 md:p-6 space-y-3" data-testid="contact-form">
      <div className="grid md:grid-cols-2 gap-3">
        <input required placeholder="Your name" value={form.name} onChange={set("name")} className="glass-input h-10 rounded-lg px-3 text-sm text-white/95 w-full" data-testid="contact-name" />
        <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="glass-input h-10 rounded-lg px-3 text-sm text-white/95 w-full" data-testid="contact-email" />
      </div>
      <input placeholder="Restaurant / publication (optional)" value={form.company} onChange={set("company")} className="glass-input h-10 rounded-lg px-3 text-sm text-white/95 w-full" data-testid="contact-company" />
      <textarea required rows={4} placeholder="How can we help?" value={form.message} onChange={set("message")} className="glass-input rounded-lg px-3 py-2 text-sm text-white/95 w-full resize-none" data-testid="contact-message" />
      <div className="flex justify-end">
        <Button type="submit" disabled={sending} className="gap-2" data-testid="contact-submit">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          Send message
        </Button>
      </div>
    </form>
  );
}

export default function Press() {
  const shareTitle = "CulinaireSpecialist: precision menu-costing for chefs";
  const [contributors, setContributors] = useState([]);
  const [mentions, setMentions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [cRes, mRes] = await Promise.all([
          api.get("/public/contributors"),
          api.get("/public/media-mentions"),
        ]);
        setContributors(cRes.data);
        setMentions(mRes.data);
      } catch { /* non-fatal: page still renders */ }
    })();
  }, []);
  const featured = contributors.filter((c) => c.featured);
  const rest = contributors.filter((c) => !c.featured);
  return (
    <div className="min-h-screen" data-testid="press-page">
      <header className="border-b border-border sticky top-0 z-30 backdrop-blur bg-background/85">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full border gold-hair flex items-center justify-center">
              <ChefHat className="w-4 h-4 gold" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-lg leading-tight">CulinaireSpecialist</div>
              <div className="label-eyebrow text-[0.55rem]">Press</div>
            </div>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/blog" className="text-muted-foreground hover:text-foreground">Journal</Link>
            <Link to="/press" className="gold">Press</Link>
            <Link to="/login" className="text-muted-foreground hover:gold">Sign in</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16 space-y-24">
        {/* Hero */}
        <section className="grid md:grid-cols-[1.2fr_1fr] gap-12 items-end">
          <div>
            <div className="label-eyebrow gold mb-4">Media kit</div>
            <h1 className="font-display text-5xl md:text-7xl leading-[1.02] mb-6">
              A precision tool<br/>for the modern <span className="gold">brigade</span>.
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
              Logos, screenshots, boilerplate, and quotes for journalists, culinary schools, and hospitality press. Anything else you need: ask.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild className="gap-2" data-testid="press-media-kit-btn">
                <a href="#assets"><Download className="w-4 h-4" /> Download assets</a>
              </Button>
              <Button variant="outline" asChild className="gap-2" data-testid="press-contact-btn">
                <a href="mailto:press@culinairespecialist.com"><Mail className="w-4 h-4" /> press@culinairespecialist.com</a>
              </Button>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="grid grid-cols-2 gap-3">
              {FACTS.map((f) => (
                <div key={f.label} className="rounded-2xl glass-card p-5">
                  <div className="font-display text-3xl gold tabular">{f.value}</div>
                  <div className="label-eyebrow mt-2">{f.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Boilerplate */}
        <section>
          <div className="label-eyebrow mb-4">Boilerplate</div>
          <div className="rounded-2xl glass-card p-8">
            <p className="text-lg font-display leading-relaxed">{BOILERPLATE}</p>
            <div className="luxury-divider my-6" />
            <SocialShare url="/press" title={shareTitle} hashtags={["culinaire", "chef", "hospitality", "brigade", "culinaryschool"]} testId="press-share" />
          </div>
        </section>

        {/* Assets */}
        <section id="assets">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="label-eyebrow gold mb-2">High-res assets</div>
              <h2 className="font-display text-3xl">Product photography</h2>
            </div>
            <a href="/logo512.png" download className="text-sm gold inline-flex items-center gap-1 hover:underline" data-testid="press-logo-download">
              Download logo <Download className="w-3 h-3" />
            </a>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {SCREENSHOTS.map((s, i) => (
              <a key={i} href={s.src} target="_blank" rel="noopener noreferrer" className="group rounded-2xl border border-border overflow-hidden card-hover bg-card" data-testid={`press-asset-${i}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={s.src} alt={s.label} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" style={{transitionDuration:"400ms"}} />
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div className="text-sm">{s.label}</div>
                  <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Executive Chef Contributors */}
        <section data-testid="press-contributors-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="label-eyebrow gold mb-2">Executive Chef Contributors</div>
              <h2 className="font-display text-3xl">Guest voices from the pass</h2>
              <p className="text-muted-foreground text-sm mt-2 max-w-xl">Working chefs. Every piece is written from the pass, not the lecture hall.</p>
            </div>
          </div>
          {contributors.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-muted-foreground">
              First guest chefs land soon. To be featured, write to <a href="mailto:concierge@culinairespecialist.com" className="gold hover:underline">concierge@culinairespecialist.com</a>.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="press-contributors-grid">
              {[...featured, ...rest].map((c) => (
                <div key={c.id} className="glass-card rounded-2xl p-5 flex gap-4" data-testid={`press-contributor-${c.id}`}>
                  {c.photo_url ? (
                    <img src={resolvePhoto(c.photo_url)} alt={c.name} className="w-16 h-16 rounded-full object-cover shrink-0" />
                  ) : (
                    <img src={chefPortraitFor(c)} alt={c.name} loading="lazy" className="w-16 h-16 rounded-full object-cover shrink-0" style={{ filter: "saturate(0.9) contrast(1.05)" }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <div className="font-display text-lg truncate">{c.name}</div>
                      {c.featured && <Star className="w-3 h-3 fill-primary text-primary" />}
                    </div>
                    <div className="text-xs gold truncate">{c.title}</div>
                    {c.bio && <p className="text-xs text-muted-foreground mt-2 line-clamp-3">{c.bio}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Media mentions */}
        <section data-testid="press-media-section">
          <div className="label-eyebrow gold mb-2 flex items-center gap-2"><Newspaper className="w-3 h-3" /> Media mentions</div>
          <h2 className="font-display text-3xl mb-6">In the news</h2>
          {mentions.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-muted-foreground">
              Coverage lands here. Press enquiries: <a href="mailto:concierge@culinairespecialist.com" className="gold hover:underline">concierge@culinairespecialist.com</a>.
            </div>
          ) : (
            <ul className="divide-y divide-white/5 glass-card rounded-2xl" data-testid="press-mentions-list">
              {mentions.map((m) => (
                <li key={m.id} className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-xs label-eyebrow gold flex items-center gap-1">
                      {m.outlet} • {m.date}
                      {m.featured && <Star className="w-3 h-3 fill-primary text-primary" />}
                    </div>
                    <div className="text-sm mt-1 font-medium">{m.title}</div>
                    {m.blurb && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{m.blurb}</div>}
                  </div>
                  <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-xs gold hover:underline inline-flex items-center gap-1 shrink-0">Read <ExternalLink className="w-3 h-3" /></a>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Contact form */}
        <section data-testid="press-contact-section">
          <div className="label-eyebrow gold mb-2 flex items-center gap-2"><Mail className="w-3 h-3" /> Get in touch</div>
          <h2 className="font-display text-3xl mb-6">Press, partnerships, or a guest byline</h2>
          <ContactForm />
        </section>

        {/* Quote */}
        <section className="max-w-3xl mx-auto text-center">
          <div className="label-eyebrow gold mb-4">Founder's note</div>
          <p className="font-display text-3xl md:text-4xl leading-tight">
            "Cost math should be a chef's ally, not a burden. Every gram we account for is a gram of creative freedom back on the pass."
          </p>
          <div className="text-sm text-muted-foreground mt-6">The CulinaireSpecialist team. Sydney.</div>
        </section>

        <div className="luxury-divider" />

        <footer className="text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-4 pb-8">
          <div>© 2026 CulinaireSpecialist. A precision tool for the culinary brigade.</div>
          <div className="flex items-center gap-4">
            <Link to="/blog" className="hover:text-foreground">Journal</Link>
            <Link to="/login" className="hover:text-foreground">Sign in</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
